import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_URL } from '../../config/apiConfig';
import { FaSave, FaHistory, FaSpinner } from 'react-icons/fa';

const BookingSettings = () => {
  const [settings, setSettings] = useState({
    assignOperatorOnBookingComplete: false,
    shareCabDriverDetailsMinutesBefore: 30,
    freeCancellationHoursBeforeDeparture: 1,
    waitingTimeUpdateIntervalMinutes: 45,
    enableRealTimeTracking: true,
    enableSMSNotifications: true,
    enableEmailNotifications: true,
    gstPercentage: 18,
    codWalletLimit: -500,
    advancePercentage: 20,
    enableVendorModule: true,
    enableAdminDriverModule: true,
    roundTripMinKmPerDay: 300,
  });

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/booking-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching booking settings:', error);
      toast.error('Failed to load booking settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/booking-settings/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setHistory(response.data.data);
        setShowHistory(true);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Failed to load history');
    }
  };

  const handleInputChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (settings.shareCabDriverDetailsMinutesBefore < 0 || settings.shareCabDriverDetailsMinutesBefore > 120) {
      toast.error('Share driver details minutes must be between 0 and 120');
      return;
    }
    if (settings.freeCancellationHoursBeforeDeparture < 0 || settings.freeCancellationHoursBeforeDeparture > 24) {
      toast.error('Free cancellation hours must be between 0 and 24');
      return;
    }
    if (settings.waitingTimeUpdateIntervalMinutes < 1 || settings.waitingTimeUpdateIntervalMinutes > 120) {
      toast.error('Waiting time update interval must be between 1 and 120');
      return;
    }
    if (settings.gstPercentage < 0 || settings.gstPercentage > 100) {
      toast.error('GST percentage must be between 0 and 100');
      return;
    }
    if (settings.codWalletLimit > 0 || settings.codWalletLimit < -100000) {
      toast.error('COD wallet limit must be between -100000 and 0 (negative number)');
      return;
    }
    if (settings.advancePercentage < 0 || settings.advancePercentage > 100) {
      toast.error('Advance payment percentage must be between 0 and 100');
      return;
    }

    if (settings.roundTripMinKmPerDay < 1 || settings.roundTripMinKmPerDay > 5000) {
      toast.error('Round Trip Minimum KM per Day must be between 1 and 5000');
      return;
    }


    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_URL}/admin/booking-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        toast.success('Booking settings updated successfully');
        setShowHistory(false);
      }
    } catch (error) {
      console.error('Error updating booking settings:', error);
      toast.error(error.response?.data?.message || 'Failed to update booking settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center min-h-96">
        <FaSpinner className="animate-spin text-3xl text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Booking Settings</h1>
        <p className="text-gray-600">Configure booking system behavior and notifications</p>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Toggle Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center p-4 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assign Operator on Booking Complete
                </label>
                <p className="text-xs text-gray-500">Automatically assign operator when booking is completed</p>
              </div>
              <input
                type="checkbox"
                checked={settings.assignOperatorOnBookingComplete}
                onChange={(e) => handleInputChange('assignOperatorOnBookingComplete', e.target.checked)}
                className="w-6 h-6 text-blue-600 rounded cursor-pointer"
              />
            </div>

            <div className="flex items-center p-4 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Enable Real-Time Tracking
                </label>
                <p className="text-xs text-gray-500">Allow customers to track rides in real-time</p>
              </div>
              <input
                type="checkbox"
                checked={settings.enableRealTimeTracking}
                onChange={(e) => handleInputChange('enableRealTimeTracking', e.target.checked)}
                className="w-6 h-6 text-blue-600 rounded cursor-pointer"
              />
            </div>

            <div className="flex items-center p-4 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Enable SMS Notifications
                </label>
                <p className="text-xs text-gray-500">Send SMS updates to customers</p>
              </div>
              <input
                type="checkbox"
                checked={settings.enableSMSNotifications}
                onChange={(e) => handleInputChange('enableSMSNotifications', e.target.checked)}
                className="w-6 h-6 text-blue-600 rounded cursor-pointer"
              />
            </div>

            <div className="flex items-center p-4 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Enable Email Notifications
                </label>
                <p className="text-xs text-gray-500">Send email updates to customers</p>
              </div>
              <input
                type="checkbox"
                checked={settings.enableEmailNotifications}
                onChange={(e) => handleInputChange('enableEmailNotifications', e.target.checked)}
                className="w-6 h-6 text-blue-600 rounded cursor-pointer"
              />
            </div>
          </div>

          {/* Numeric Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Share Cab Driver Details Before (minutes)
              </label>
              <input
                type="number"
                min="0"
                max="120"
                value={settings.shareCabDriverDetailsMinutesBefore}
                onChange={(e) => handleInputChange('shareCabDriverDetailsMinutesBefore', parseInt(e.target.value) || 0)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Minutes before departure to share driver details (0-120)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Free Cancellation Hours Before Departure
              </label>
              <input
                type="number"
                min="0"
                max="24"
                value={settings.freeCancellationHoursBeforeDeparture}
                onChange={(e) => handleInputChange('freeCancellationHoursBeforeDeparture', parseInt(e.target.value) || 0)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Hours before departure for free cancellation (0-24)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Waiting Time Update Interval (minutes)
              </label>
              <input
                type="number"
                min="1"
                max="120"
                value={settings.waitingTimeUpdateIntervalMinutes}
                onChange={(e) => handleInputChange('waitingTimeUpdateIntervalMinutes', parseInt(e.target.value) || 1)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Interval for updating waiting time (1-120)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                GST/Tax Percentage (%)
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settings.gstPercentage}
                  onChange={(e) => handleInputChange('gstPercentage', parseFloat(e.target.value) || 0)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-xl font-semibold text-gray-700">%</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">GST/Tax percentage applied to all bookings (0-100)</p>
              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                <strong>Tax amount calculation:</strong> Base Fare × {settings.gstPercentage}% = Tax Amount
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                COD Wallet Limit (₹)
              </label>
              <input
                type="number"
                min="-100000"
                max="0"
                value={settings.codWalletLimit}
                onChange={(e) => handleInputChange('codWalletLimit', parseInt(e.target.value) || -500)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Minimum wallet balance for COD rides (negative, e.g., -500)</p>
              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                <strong>Note:</strong> Drivers cannot accept COD rides if balance falls below this limit (Range: -100000 to 0)
              </div>
            </div>
          </div>

          {/* Advance Payment Settings */}
          <div className="pt-6 border-t">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              💰 Advance Payment
            </h2>
            <div className="max-w-md">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Advance Payment Percentage (%)
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={settings.advancePercentage}
                  onChange={(e) => handleInputChange('advancePercentage', parseFloat(e.target.value) || 0)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-xl font-semibold text-gray-700">%</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Percentage of total fare collected as advance (0-100)</p>
              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
                <strong>Example:</strong> If fare is ₹1000 & advance is {settings.advancePercentage}%, customer pays ₹{(1000 * (settings.advancePercentage || 0) / 100).toFixed(0)} upfront
              </div>
            </div>
          </div>

          {/* Round Trip Minimum KM Setting */}
          <div className="pt-6 border-t">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              🔄 Round Trip Pricing
            </h2>
            <div className="max-w-md">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum KM per Day for Round Trips
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min="1"
                  max="5000"
                  step="1"
                  value={settings.roundTripMinKmPerDay}
                  onChange={(e) => handleInputChange('roundTripMinKmPerDay', parseInt(e.target.value) || 300)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-lg font-semibold text-gray-700">km/day</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Minimum distance charged per day for round trips (1–5000 km)</p>
              <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-700">
                <strong>Rule:</strong> If actual distance ÷ trip days is less than {settings.roundTripMinKmPerDay} km/day, the price will be charged at {settings.roundTripMinKmPerDay} km × days × per-km rate.
              </div>
            </div>
          </div>

          {/* Modules Toggle Settings */}
          <div className="pt-6 border-t">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              🔌 Modules & Features Toggle
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Enable Vendor Module
                  </label>
                  <p className="text-xs text-gray-500">Enable or disable the entire Vendor and Vendor Driver module, including partner logins and registrations</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.enableVendorModule !== false}
                  onChange={(e) => handleInputChange('enableVendorModule', e.target.checked)}
                  className="w-6 h-6 text-blue-600 rounded cursor-pointer"
                />
              </div>

              <div className="flex items-center p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Enable Admin Driver Module
                  </label>
                  <p className="text-xs text-gray-500">Enable or disable the Admin Driver module, including direct driver logins and registrations</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.enableAdminDriverModule !== false}
                  onChange={(e) => handleInputChange('enableAdminDriverModule', e.target.checked)}
                  className="w-6 h-6 text-blue-600 rounded cursor-pointer"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-6 border-t">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {saving ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <FaSave />
                  Save Settings
                </>
              )}
            </button>

            <button
              type="button"
              onClick={fetchHistory}
              className="flex items-center gap-2 px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
            >
              <FaHistory />
              View History
            </button>
          </div>
        </form>
      </div>

      {/* History Modal */}
      {showHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-96 overflow-auto">
            <div className="sticky top-0 bg-gray-100 px-6 py-4 flex justify-between items-center border-b">
              <h2 className="text-lg font-bold text-gray-800">Settings Change History</h2>
              <button
                onClick={() => setShowHistory(false)}
                className="text-gray-600 hover:text-gray-900 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="p-6">
              {history.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No history available</p>
              ) : (
                <div className="space-y-4">
                  {history.map((record, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <p className="text-sm font-semibold text-gray-700 mb-2">
                        {new Date(record.createdAt).toLocaleString()}
                      </p>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600">Assign Operator: {record.assignOperatorOnBookingComplete ? 'Yes' : 'No'}</p>
                          <p className="text-gray-600">Driver Details: {record.shareCabDriverDetailsMinutesBefore} min</p>
                          <p className="text-gray-600">Free Cancellation: {record.freeCancellationHoursBeforeDeparture} hrs</p>
                          <p className="text-gray-600 font-semibold text-blue-600">GST: {record.gstPercentage || 18}%</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Update Interval: {record.waitingTimeUpdateIntervalMinutes} min</p>
                          <p className="text-gray-600">Real-Time: {record.enableRealTimeTracking ? 'Yes' : 'No'}</p>
                          <p className="text-gray-600">SMS: {record.enableSMSNotifications ? 'Yes' : 'No'}</p>
                          <p className="text-gray-600">Email: {record.enableEmailNotifications ? 'Yes' : 'No'}</p>
                          <p className="text-gray-600 font-semibold text-amber-600">COD Limit: ₹{record.codWalletLimit || -500}</p>
                          <p className="text-gray-600 font-semibold text-green-600">Advance: {record.advancePercentage || 20}%</p>
                          <p className="text-gray-600 font-semibold text-purple-600">Vendor Module: {record.enableVendorModule !== false ? 'Enabled' : 'Disabled'}</p>
                          <p className="text-gray-600 font-semibold text-indigo-600">Admin Driver: {record.enableAdminDriverModule !== false ? 'Enabled' : 'Disabled'}</p>
                          <p className="text-gray-600 font-semibold text-orange-600">Min KM/Day (RT): {record.roundTripMinKmPerDay || 300} km</p>
                        </div>
                      </div>
                      {record.updatedBy && (
                        <p className="text-xs text-gray-500 mt-2">Updated by: {record.updatedBy.name}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingSettings;
